import {
  APP_INIT_DEFERRED,
  AUTH_ACTIVATE_ACCOUNT,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  ORGANIZATION_SWITCH,
} from '../actions';
import {
  fetchAvailableOrganizations,
  rememberSelectedOrganization,
  resolveOrganizationSelection,
} from '../actions/available-organizations.actions';
import { updateDeviceFlags } from '../actions/deviceFlags.actions';
import { getUserId } from '../selectors/user.selector';
import { DEVICE_FLAGS } from '../../constants/deviceFlags';
import userService from '../../services/user.service';

// Cold starts (APP_IS_READY) are deliberately not gated: the session token
// already carries an organization.
const GATE_ACTIONS = [AUTH_LOGIN];
const FETCH_ONLY_ACTIONS = [APP_INIT_DEFERRED, AUTH_ACTIVATE_ACCOUNT];

export const onAuthenticationProcessDone =
  ({ dispatch }) =>
  next =>
  async action => {
    next(action);

    const isGateAction = GATE_ACTIONS.includes(action.type);
    if (!isGateAction && !FETCH_ONLY_ACTIONS.includes(action.type)) return;

    if (!userService.isAuthenticated) return false;

    if (isGateAction) {
      dispatch(resolveOrganizationSelection());
      return;
    }

    dispatch(fetchAvailableOrganizations());
  };

// Sole writer of the "last used" org: every switch, whatever its entry point.
export const rememberSwitchedOrganization =
  ({ dispatch }) =>
  next =>
  action => {
    next(action);

    if (action.type !== ORGANIZATION_SWITCH) return;

    // From the action — the organization slice still holds the previous org here.
    const organizationId = action.payload?.organizationId;
    if (organizationId) dispatch(rememberSelectedOrganization({ organizationId }));
  };

// Bounds the per-user map: logout keeps only the departing user's entry — the
// value is just a pre-selection hint.
export const pruneRememberedOrganizations =
  ({ dispatch, getState }) =>
  next =>
  action => {
    if (action.type !== AUTH_LOGOUT) return next(action);

    // Read before the reducers clear the auth slice.
    const state = getState();
    const userId = getUserId(state);
    const remembered = state.deviceFlags?.[DEVICE_FLAGS.SELECTED_ORGANIZATION_BY_USER] || {};

    next(action);

    const pruned = userId && remembered[userId] ? { [userId]: remembered[userId] } : {};
    if (Object.keys(remembered).length > Object.keys(pruned).length) {
      dispatch(
        updateDeviceFlags({ key: DEVICE_FLAGS.SELECTED_ORGANIZATION_BY_USER, state: pruned }),
      );
    }
  };

export const availableOrganizationsMiddleware = [
  onAuthenticationProcessDone,
  rememberSwitchedOrganization,
  pruneRememberedOrganizations,
];
